import type { ButtonProps } from "@v1/components/designSystem/button/Button";

import * as React from "react";

import twMerge from "@replo/design-system/utils/twMerge";
import DeprecatedButton from "@v1/components/designSystem/button/Button";
import { exhaustiveSwitch } from "replo-utils/lib/misc";

type BaseEmptyStateProps = {
  title: string;
  description?: string;
  size?: "base" | "sm";
  layoutClassName?: string;
  UNSAFE_className?: string;
  actionButtonProps?: ButtonProps | null;
};

type DefaultEmptyStateProps = BaseEmptyStateProps & {
  icon?: React.ReactNode;
  image?: React.ReactNode;
  variant?: "primary" | "secondary" | "noRings";
};

type PingingEmptyStateProps = BaseEmptyStateProps & {
  variant?: "pinging";
};

type EmptyStateProps = DefaultEmptyStateProps | PingingEmptyStateProps;

/**
 * @deprecated Use Empty from @replo/design-system/components/empty/Empty instead.
 *
 * Migration notes:
 * - image -> media
 * - layoutClassName -> className
 * - actionButtonProps -> use children with v2 Button
 * - variant prop removed (rings controlled by size in new component)
 */
// Note (Cole, 2025-02-18): The rings are hardcoded, so if you want to use this somewhere that the rings need to be different, you'll have to add a new variant.
const DeprecatedEmptyState: React.FC<
  React.PropsWithChildren<EmptyStateProps>
> = ({
  title,
  description,
  children,
  size = "base",
  layoutClassName,
  UNSAFE_className,
  variant = "primary",
  actionButtonProps,
  ...props
}) => {
  const renderPrimaryContent = (value: {
    variant: "primary" | "secondary" | "pinging" | "noRings";
    icon?: React.ReactNode;
    image?: React.ReactNode;
  }) => (
    <>
      {value.variant !== "noRings" && (
        <>
          <div
            className={twMerge(
              "border-border absolute h-[149px] w-[149px] rounded-full border-[.8px] border-dashed",
              size === "sm" && "hidden h-0 w-0",
              size === "base" && "h-[149px] w-[149px]",
            )}
          />
          <div
            className={twMerge(
              "border-border absolute h-[218px] w-[218px] rounded-full border-[.8px] border-dashed",
              size === "sm" && "hidden h-0 w-0",
              size === "base" && "h-[218px] w-[218px]",
            )}
          />
          <div
            className={twMerge(
              "absolute bottom-[-80px] left-[50%] z-10 translate-x-[-50%]",
              variant === "primary" && "bg-white",
              variant === "secondary" && "bg-neutral-soft",
              size === "sm" && "hidden h-0 w-0",
              size === "base" && "h-[80px] w-[200px]",
            )}
          />
        </>
      )}

      {value.image ? (
        <div className="relative z-10 flex items-center justify-center">
          {value.image}
        </div>
      ) : (
        <div
          className={twMerge(
            "bg-sky-soft text-primary relative z-10 flex items-center justify-center rounded-full",
            size === "sm" && "h-12 w-12 text-lg",
            size === "base" && "h-16 w-16 text-2xl",
          )}
        >
          {value.icon}
        </div>
      )}
    </>
  );

  const startEnhancer = exhaustiveSwitch(
    { ...props, variant },
    "variant",
  )({
    primary: renderPrimaryContent,
    secondary: renderPrimaryContent,
    pinging: () => {
      return (
        <div className="relative m-4 flex h-10 w-10 items-center justify-center duration-1000">
          <div className="animate-ping-slow bg-primary absolute inline-flex h-full w-full rounded-full opacity-75"></div>
          <div className="bg-primary z-10 h-4 w-4 rounded-full pl-3 pt-3"></div>
        </div>
      );
    },
    noRings: renderPrimaryContent,
  });

  return (
    <div
      className={twMerge(
        "flex flex-col items-center justify-center gap-2",
        layoutClassName,
        UNSAFE_className,
      )}
    >
      <div className="relative flex items-center justify-center">
        {startEnhancer}
      </div>
      <div
        className={twMerge(
          "relative z-10 flex flex-col items-center justify-center gap-2",
          variant === "primary" && "bg-white",
          variant === "secondary" && "bg-neutral-soft",
          variant === "pinging" && "bg-transparent",
          variant === "noRings" && "bg-transparent",
        )}
      >
        <div className="flex max-w-[300px] flex-col gap-1 text-center">
          <div
            className={twMerge(
              "text-default break-words",
              size === "sm" && "text-xs font-semibold",
              size === "base" && "header-h6",
            )}
          >
            {title}
          </div>
          {description && (
            <div
              className={twMerge(
                "text-placeholder break-words",
                size === "sm" && "text-xs",
                size === "base" && "text-sm",
              )}
            >
              {description}
            </div>
          )}
        </div>
        {actionButtonProps ? <DeprecatedButton {...actionButtonProps} /> : null}
        {children}
      </div>
    </div>
  );
};

export { DeprecatedEmptyState };
